import React from "react";
import { LogoFull } from "@/components/landing/Logo";

// 6-slide Instagram carousel for the "Introducing Uberti Creative" post.
// Each slide is rendered at 1080x1080 (Instagram square) so we can capture
// pixel-perfect PNGs with Playwright.

const Slide = ({ index, children, testid }) => (
  <section
    data-testid={testid || `slide-${index}`}
    className="relative shrink-0 overflow-hidden"
    style={{ width: "1080px", height: "1080px", background: "#000" }}
  >
    {/* Subtle gold radial glow */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(60% 80% at 50% 0%, rgba(198,162,99,0.10) 0%, transparent 60%), radial-gradient(60% 80% at 50% 100%, rgba(198,162,99,0.06) 0%, transparent 60%)",
      }}
    />
    {/* Hairline frame */}
    <div
      aria-hidden
      className="absolute inset-12 border border-[rgba(198,162,99,0.22)] rounded-2xl pointer-events-none"
    />
    {/* Slide index pill */}
    <div className="absolute top-20 right-20 text-[14px] tracking-[0.4em] uppercase text-[rgb(var(--gold-soft))]">
      0{index} / 06
    </div>
    {/* Footer brand line */}
    <div className="absolute bottom-20 inset-x-0 flex items-center justify-between px-24">
      <div className="text-[14px] tracking-[0.4em] uppercase text-[rgb(var(--bone-dim))]">
        Uberti Creative
      </div>
      <div className="text-[14px] tracking-[0.4em] uppercase text-[rgb(var(--gold-soft))]">
        @UbertiCreative
      </div>
    </div>

    <div className="relative w-full h-full px-24 flex flex-col justify-center">
      {children}
    </div>
  </section>
);

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-4 text-[14px] tracking-[0.4em] uppercase text-[rgb(var(--bone-dim))]">
    <span className="h-px w-12 bg-[rgb(var(--gold))]" />
    {children}
  </div>
);

export default function IntroducingPost() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[rgb(var(--bone))] font-sans-body py-12">
      <div className="max-w-[1200px] mx-auto px-6">
        <h1 className="font-serif-display text-4xl md:text-5xl text-[rgb(var(--bone))]">
          Carousel · Introducing Uberti Creative
        </h1>
        <p className="mt-3 text-[rgb(var(--bone-dim))]">
          Six 1080×1080 slides. Right-click any slide to save as PNG, or use
          the downloaded files in <code>/posts/</code>.
        </p>
      </div>

      <div className="mt-12 flex flex-col items-center gap-10">
        {/* SLIDE 1 — INTRODUCING */}
        <Slide index={1}>
          <div className="flex flex-col items-start">
            <SectionTag>Introducing</SectionTag>
            <div className="mt-12">
              <LogoFull className="h-[160px] w-[640px]" />
            </div>
            <h2 className="mt-16 font-serif-display font-medium text-[88px] leading-[1.04] text-[rgb(var(--bone))]">
              Introducing
              <br />
              <span className="italic text-[rgb(var(--gold-soft))]">
                Uberti Creative
              </span>
            </h2>
            <p className="mt-10 max-w-[760px] font-serif-display text-[34px] leading-[1.4] text-[rgb(var(--bone-dim))]">
              Strategic content &amp; digital media for brands, businesses,
              teams, and creators.
            </p>
          </div>
        </Slide>

        {/* SLIDE 2 — WHAT WE DO */}
        <Slide index={2}>
          <SectionTag>01 · What We Do</SectionTag>
          <h2 className="mt-10 font-serif-display font-medium text-[100px] leading-[1.02] text-[rgb(var(--bone))]">
            What we{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">do</span>.
          </h2>
          <p className="mt-12 max-w-[820px] font-serif-display text-[36px] leading-[1.5] text-[rgb(var(--bone-dim))]">
            Uberti Creative helps clients strengthen their online presence
            through content creation, brand direction, short-form video, and
            social media strategy.
          </p>
        </Slide>

        {/* SLIDE 3 — SERVICES */}
        <Slide index={3}>
          <SectionTag>02 · Services</SectionTag>
          <h2 className="mt-8 font-serif-display font-medium text-[100px] leading-[1.02] text-[rgb(var(--bone))]">
            Our{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">
              services
            </span>
            .
          </h2>
          <ul className="mt-10 space-y-3">
            {[
              "Social Media Content",
              "Short-Form Video",
              "Brand Direction",
              "Online Presence Audits",
              "Sports & Team Media",
              "Website Feedback",
            ].map((s, i) => (
              <li
                key={s}
                className="flex items-baseline gap-6 border-b border-[rgba(198,162,99,0.18)] py-3"
              >
                <span className="font-cinzel text-[22px] text-[rgb(var(--gold-soft))]">
                  0{i + 1}
                </span>
                <span className="font-serif-display text-[40px] text-[rgb(var(--bone))]">
                  {s}
                </span>
              </li>
            ))}
          </ul>
        </Slide>

        {/* SLIDE 4 — WHO WE WORK WITH */}
        <Slide index={4}>
          <SectionTag>03 · Who We Work With</SectionTag>
          <h2 className="mt-8 font-serif-display font-medium text-[96px] leading-[1.02] text-[rgb(var(--bone))]">
            Who we{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">
              work with
            </span>
            .
          </h2>
          <ul className="mt-10 space-y-3">
            {[
              "Local Businesses",
              "Product Brands",
              "Restaurants & Food Brands",
              "Sports Teams & Athletes",
              "Creators & Personal Brands",
            ].map((s, i) => (
              <li
                key={s}
                className="flex items-baseline gap-6 border-b border-[rgba(198,162,99,0.18)] py-3"
              >
                <span className="font-cinzel text-[22px] text-[rgb(var(--gold-soft))]">
                  0{i + 1}
                </span>
                <span className="font-serif-display text-[42px] text-[rgb(var(--bone))]">
                  {s}
                </span>
              </li>
            ))}
          </ul>
        </Slide>

        {/* SLIDE 5 — WHY IT MATTERS */}
        <Slide index={5}>
          <SectionTag>04 · Why It Matters</SectionTag>
          <h2 className="mt-10 font-serif-display font-medium text-[100px] leading-[1.02] text-[rgb(var(--bone))]">
            Why it{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">matters</span>
            .
          </h2>
          <p className="mt-12 max-w-[840px] font-serif-display text-[40px] leading-[1.45] text-[rgb(var(--bone))]">
            Your business may already have a great product, service, or story.
          </p>
          <p className="mt-6 max-w-[840px] font-serif-display italic text-[36px] leading-[1.45] text-[rgb(var(--gold-soft))]">
            The goal is to make sure your online presence reflects that
            quality.
          </p>
        </Slide>

        {/* SLIDE 6 — START HERE */}
        <Slide index={6}>
          <SectionTag>05 · Start Here</SectionTag>
          <h2 className="mt-10 font-serif-display font-medium text-[100px] leading-[1.02] text-[rgb(var(--bone))]">
            Start{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">here</span>.
          </h2>
          <p className="mt-12 max-w-[840px] font-serif-display text-[38px] leading-[1.45] text-[rgb(var(--bone-dim))]">
            Looking to improve your brand’s online presence?
          </p>
          <p className="mt-6 max-w-[840px] font-serif-display text-[38px] leading-[1.45] text-[rgb(var(--bone))]">
            Complete the{" "}
            <span className="italic text-[rgb(var(--gold-soft))]">
              Brand &amp; Content Discovery Form
            </span>{" "}
            in bio.
          </p>

          <div className="mt-16 inline-flex items-center gap-4 px-10 py-5 rounded-full btn-gold w-fit text-[24px] font-medium">
            Link in Bio
            <span aria-hidden>→</span>
          </div>
        </Slide>
      </div>
    </div>
  );
}
